import store from './store.js';
import { v4 as uuidv4 } from 'uuid';
import findIndex from 'lodash/findIndex';
import * as Sentry from '@sentry/browser';

import ContentEncryptionService from './components/lib/ContentEncryptionService.js';
import Decrypter from './components/lib/Decrypter.js';
import Encrypter from './components/lib/Encrypter.js';
import { notesAPI } from './communication/notes';
import { interactionEventsTracker } from './communication/analytics';
import { describeDiscards } from '../../_shared_/js/utils/richTextDiscards.js';

/** Long enough to read a list of what the host left out of the note. */
const DISCARD_TOAST_MS = 8000;

export default {
    id: null,
    uuid: null,
    confirmed: false,
    isRendered: false,
    color: '',
    colorSlug: null,
    hasAnnotation: false,

    /**
     * Set by the host only on notes written by another reader. Declared here so a
     * selection rebuilt over a previous one never inherits its author.
     */
    author: null,

    // PDF specific
    startPage: null,
    startPageStartOffset: null,
    startPageLength: null,
    endPage: null,
    endPageLength: null,

    // EPUB R1 legacy fields — used during lazy migration from R1 to R2.
    // Once all highlights have been migrated to R2 (epubR2Location has startCss),
    // these fields can be removed: epubLocationStartChar, epubLocationStartCfi,
    // epubLocationEndCfi, epubLocation.
    // Note: epubChapter is still used by R2 highlights for chapter identification.
    epubLocationStartChar: '',
    epubChapter: '',
    epubLocationStartCfi: '',
    epubLocationEndCfi: '',
    epubLocation: {},

    // @R2 EPUB R2 specific - CSS selector-based positioning
    location: null,
    epubR2Location: null,

    totalLength: null,
    truncated: true,
    copied: false,
    showCopyTooltip: false,
    totalText: null,
    // annotationText is plain text always (the server derives it from the
    // markup on rich notes); annotationHtml carries the sanitized markup and
    // is null on legacy plain notes. annotationFormat is transient request
    // state: 'html' marks the pending annotationText as editor markup for the
    // next save, and the server response resets it.
    annotationText: null,
    annotationHtml: null,
    annotationFormat: null,

    create() {
        this.uuid = uuidv4();
    },

    isStored() {
        return Object.prototype.hasOwnProperty.call(this, 'id') && this.id != null && this.id !== '';
    },

    /**
     * Determines if this highlight uses the legacy R1 format (offset-based)
     * vs the new R2 format (CSS-selector-based).
     * Legacy highlights have epubLocationStartChar but no R2 location data.
     */
    isLegacy() {
        return !this.location || !this.location?.selection;
    },

    canBeDeleted() {
        return this.isStored() || (this.isRendered && store.state.anonymousSession);
    },

    getUniqueCssClass() {
        return `uuid-${this.uuid}`;
    },

    getCssClassForMarker() {
        return `${this.getUniqueCssClass()} ${this.isStored() ? 'confirmed' : 'unconfirmed'}`;
    },

    updateAnnotationText() {
        if (!this.hasAnnotation) return;
        const index = findIndex(store.state.selections, ['uuid', this.uuid]);
        // Same silent no-op as update(): splice(-1, 1, patch) would overwrite the last entry.
        if (index === -1) return;
        store.state.selections.splice(index, 1, { ...store.state.selections[index], ...this });
    },

    async reHydrate() {
        // @R2: Reconstruct R2 location from stored epubR2Location (DB → DOM rendering format)
        if (this.epubR2Location?.startCss) {
            // Full CSS selector-based R2 (new highlights created in R2)
            this.location = {
                selection: this.epubR2Location,
            };
        } else if (this.epubR2Location?.text) {
            // @R2 Text-based migration (legacy R1 → R2 via text search)
            this.location = {
                selection: { text: this.epubR2Location.text },
            };
        }

        this.totalText = await this.maybeDecryptTotalText();
        return this;
    },

    /**
     * Whether this selection is a note (a highlight carrying annotation text)
     * or a plain highlight. Drives the interaction event entity so notes and
     * highlights are counted separately in Coniglio.
     */
    interactionEntity() {
        return this.hasAnnotation ? 'note' : 'highlight';
    },

    /**
     * The annotation fields for a save request. A pending editor save already
     * carries the markup in annotationText with the 'html' marker; an
     * untouched rich note re-sends its stored markup with the marker, so a
     * position-only update (R2 migration) never flattens it to plain text.
     */
    annotationPayload() {
        if (this.annotationFormat === 'html') {
            return { annotationText: this.annotationText, annotationFormat: 'html' };
        }

        if (this.annotationHtml) {
            return { annotationText: this.annotationHtml, annotationFormat: 'html' };
        }

        return { annotationText: this.annotationText };
    },

    /**
     * Adopts the server's split of the saved annotation: annotationText comes
     * back as plain text (v1 store/update responses use snake_case keys, the
     * list uses camelCase) and the markup, when any, in annotationHtml.
     */
    syncAnnotationFromResponse(response) {
        this.annotationText = response?.annotation_text ?? response?.annotationText ?? this.annotationText;
        this.annotationHtml = response?.annotation_html ?? response?.annotationHtml ?? null;
        this.annotationFormat = null;
        this.reportDiscards(response?.sanitization?.discarded);
    },

    /**
     * What the host left out of the markup is told through a toast: the note
     * editor closes on save, so there is no editor left to show it in. The save
     * itself completed either way.
     * @param {Array<{node: string, reason: string, detail: string|null}>|undefined} discarded
     */
    reportDiscards(discarded) {
        if (!discarded?.length) return;

        const labels = store.state.localizations?.[store.state.lang]?.richText?.discard;
        if (!labels) return;

        const lines = describeDiscards(discarded, labels);
        store.mutations.openToast(store.state, {
            text: `${labels.intro} ${lines.join(' · ')}`,
            durationMs: DISCARD_TOAST_MS,
        });
    },

    async store() {
        const encryptedText = await this.maybeEncryptTotalText();
        const response = await notesAPI.store({ ...this, totalText: encryptedText, ...this.annotationPayload() });
        this.confirmed = true;
        this.id = response.id;
        this.syncAnnotationFromResponse(response);
        store.state.selections.push(this);
        interactionEventsTracker.track(this.interactionEntity(), 'create', this.id);
    },

    async update() {
        try {
            const response = await notesAPI.update(this.id, {
                color: this.color,
                colorSlug: this.colorSlug,
                ...this.annotationPayload(),
            });

            this.syncAnnotationFromResponse(response);

            const index = findIndex(store.state.selections, ['uuid', this.uuid]);
            if (index === -1) return;
            store.state.selections.splice(index, 1, {
                ...store.state.selections[index],
                color: this.color,
                colorSlug: response?.color_slug ?? response?.colorSlug ?? this.colorSlug,
                annotationText: this.annotationText,
                annotationHtml: this.annotationHtml,
            });
        } catch (e) {
            console.error('highlight update failed', e);
            Sentry.captureException(e, {
                tags: { feature: 'highlights' },
                extra: { method: 'update', highlightId: this.id },
            });
            throw e;
        }
    },

    /**
     * @R2 Lazy migration: persist R2 location data to DB after successful text-based rendering.
     * Called from readium2.js when a legacy R1 highlight renders successfully via text search.
     * This converts the highlight from R1 format to R2 text-based format in the database.
     */
    async persistR2Migration(epubR2Location) {
        // Skip if not stored in DB, or already migrated
        if (!this.isStored() || this.epubR2Location) return;

        try {
            await notesAPI.update(this.id, {
                color: this.color,
                ...this.annotationPayload(),
                position: {
                    type: 'epub',
                    epubR2Location,
                    epubChapter: this.epubChapter,
                },
            });
            this.epubR2Location = epubR2Location;
        } catch (e) {
            console.error('persistR2Migration failed', this.uuid?.substring(0, 8), e);
        }
    },

    /**
     * Deletes this highlight/note from the host application.
     * @returns {Promise<boolean>} True if the deletion succeeded, false on failure
     */
    async destroy() {
        try {
            await notesAPI.delete(this.id);
            return true;
        } catch (e) {
            console.error('highlight destroy failed', e);
            Sentry.captureException(e, {
                tags: { feature: 'highlights' },
                extra: { method: 'destroy', highlightId: this.id },
            });
            return false;
        }
    },

    async maybeDecryptTotalText() {
        /**
         * If content encryption is enabled, try to decrypt the highlight raw text
         */
        if (ContentEncryptionService.shouldDecrypt()) {
            return await Decrypter.decryptText(this.totalText);
        }

        return this.totalText;
    },

    async maybeEncryptTotalText() {
        /**
         * If content encryption is enabled, try to decrypt the highlight raw text
         */
        if (ContentEncryptionService.shouldEncrypt()) {
            return await Encrypter.encryptText(this.totalText);
        }

        return this.totalText;
    },
};
